//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { BodyErrorText } from '@components/atoms/text/Text';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
} from '@components/molecules/pagination/TablePaginationControls';
import Cuid from '@models/Cuid';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { UIAFooter } from '@uia/layout/UIAFooter';
import { ProductsContainer } from '@uia/products/ProductsContainer';
import { useProductCatalogsQuery } from '@uia/products/hooks/useProductCatalogsQuery';
import { useProductsQuery } from '@uia/products/hooks/useProductsQuery';
import { getIsUiaProductsApiPortEnabled } from '@uia/products/selectors/getIsUiaProductsApiPortEnabled';
import { isDefined } from '@utils/filterPredicates';

export interface UIAProductsProps {
  cuid: Cuid;
  onClose: () => void;
}

export const UIAProducts = ({ cuid, onClose }: UIAProductsProps) => {
  // TODO: PD-263077 Use setSearchQuery after migration of universal-image-actions-productize-editor
  const [searchQuery, setSearchQuery] = useState('');
  const [limit, setLimit] = useState<number>(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState<number>(DEFAULT_PAGE_NUMBER);
  const { data: metadata, isError, isLoading } = useMediaMetadataQuery(cuid);
  const { data: catalogs } = useProductCatalogsQuery();

  const hasCatalogConnected = isDefined(catalogs) && catalogs.length > 0;

  // TODO: PD-282907 - Remove the feature flag once UIA Products Api Port is complete
  const isUiaProductsApiPortEnabled = useSelector(
    getIsUiaProductsApiPortEnabled,
  );

  // TODO: PD-263077 Use products in the: universal-image-actions-productize-editor
  const { data: products, isLoading: isLoadingProducts } = useProductsQuery({
    hasCatalog: hasCatalogConnected,
    isBvCampaignTagged: metadata?.isBvCampaignTagged,
    keywords: searchQuery,
    partialCuid: Cuid.toPartial(cuid),
    limit,
    offset,
    isEnabled: isUiaProductsApiPortEnabled,
  });

  const handlePageChange = (currentPage: number, itemsPerPage: number) => {
    setLimit(itemsPerPage);
    setOffset(currentPage * itemsPerPage);
  };

  return (
    <OverlayWrapper>
      {(() => {
        if (isLoading) {
          return <CenteredSpinner />;
        } else {
          if (isError) {
            return (
              <BodyErrorText>
                An unexpected error occurred. Please try again or contact
                Bazaarvoice Support.
              </BodyErrorText>
            );
          } else {
            return (
              <ProductsContainer
                cuid={cuid}
                handlePageChange={handlePageChange}
                hasCatalogConnected={hasCatalogConnected}
                isLoadingProducts={isLoadingProducts}
                limit={limit}
                metadata={metadata}
                offset={offset}
                products={products}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
              />
            );
          }
        }
      })()}
      <UIAFooter>
        <UIAFooter.Actions>
          <UIAFooter.ActionButton
            onClick={onClose}
            trackingId={UIATrackingIds.PRODUCTS_CLOSE_MODAL}
          >
            Done
          </UIAFooter.ActionButton>
        </UIAFooter.Actions>
      </UIAFooter>
    </OverlayWrapper>
  );
};

const OverlayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`;
